import React, { useRef, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import QRCode from "react-qr-code";
import { Button } from "@codegouvfr/react-dsfr/Button";
import { Input } from "@codegouvfr/react-dsfr/Input";
import TdModal from "../../common/Components/Modal/Modal";
import { DsfrNotificationError } from "../../common/Components/Error/Error";

const GENERATE_TOTP_SETUP = gql`
  mutation GenerateTotpSetup {
    generateTotpSetup {
      secret
      qrCodeUrl
    }
  }
`;

const CONFIRM_TOTP_SETUP = gql`
  mutation ConfirmTotpSetup($code: String!) {
    confirmTotpSetup(code: $code) {
      codes
    }
  }
`;

const FINALIZE_MFA_SETUP = gql`
  mutation FinalizeMfaSetup {
    finalizeMfaSetup
  }
`;

// 4 étapes : QR code et validation sont fusionnés dans une même étape
type Step = "explanation" | "qrcode" | "recovery" | "success";

type Props = {
  onSuccess: () => void;
  onClose: () => void;
};

const STEP_LABELS: Record<Step, string> = {
  explanation: "Explication",
  qrcode: "QRcode",
  recovery: "Clé de récupération",
  success: "Confirmation"
};

// Label affiché dans le stepper pour indiquer l'étape suivante
const NEXT_STEP_LABELS: Partial<Record<Step, string>> = {
  explanation: "QRcode",
  qrcode: "Clé de récupération",
  recovery: "Confirmation"
};

const STEPS: Step[] = ["explanation", "qrcode", "recovery", "success"];

export default function TotpSetupWizard({ onSuccess, onClose }: Props) {
  const [step, setStep] = useState<Step>("explanation");

  // Étape 1
  const [appInstalled, setAppInstalled] = useState(false);
  const [showAppError, setShowAppError] = useState(false);

  // Étape 2
  const [secret, setSecret] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [showManualKey, setShowManualKey] = useState(false);

  // Étape 3
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [showSavedError, setShowSavedError] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTextareaRef = useRef<HTMLTextAreaElement>(null);

  const stepIndex = STEPS.indexOf(step);
  const totalSteps = STEPS.length;
  const nextStepLabel = NEXT_STEP_LABELS[step];

  const [generateSetup, { loading: generating, error: generateError }] =
    useMutation(GENERATE_TOTP_SETUP);

  const [
    confirmSetup,
    { loading: confirming, error: confirmError, reset: resetConfirmError }
  ] = useMutation(CONFIRM_TOTP_SETUP);

  const [finalizeSetup, { loading: finalizing, error: finalizeError }] =
    useMutation(FINALIZE_MFA_SETUP);

  const goToQrCode = async () => {
    if (!appInstalled) {
      setShowAppError(true);
      return;
    }
    const { data } = await generateSetup();
    if (data?.generateTotpSetup) {
      setSecret(data.generateTotpSetup.secret);
      setQrCodeUrl(data.generateTotpSetup.qrCodeUrl);
      setStep("qrcode");
    }
  };

  const handleConfirmCode = async () => {
    const { data } = await confirmSetup({ variables: { code: totpCode } });
    if (data?.confirmTotpSetup?.codes) {
      setRecoveryCodes(data.confirmTotpSetup.codes);
      setStep("recovery");
    }
  };

  const handleCopyCodes = async () => {
    const text = recoveryCodes.join("\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback : textarea dans la modal pour éviter le focus trap
      const el = copyTextareaRef.current;
      if (el) {
        el.value = text;
        el.focus();
        el.select();
        document.execCommand("copy");
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadCodes = () => {
    const content = [
      "Codes de récupération Trackdéchets",
      "====================================",
      "Conservez ces codes en lieu sûr.",
      "Chaque code est à usage unique.",
      "",
      ...recoveryCodes
    ].join("\n");

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "trackdechets-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFinish = async () => {
    if (!savedConfirmed) {
      setShowSavedError(true);
      return;
    }
    const { data } = await finalizeSetup();
    if (data?.finalizeMfaSetup) {
      setStep("success");
    }
  };

  const title = "Activez l'authentification TOTP";
  const CopyRecoveryCodeIcone = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.3333 1.33325V2.66659H13.338C13.7033 2.66659 14 2.96325 14 3.32859V14.0046C13.9996 14.37 13.7035 14.6662 13.338 14.6666H2.662C2.29654 14.6662 2.00037 14.37 2 14.0046V3.32859C2 2.96325 2.29667 2.66659 2.662 2.66659H4.66667V1.33325H11.3333ZM4.66667 3.99992H3.33333V13.3333H12.6667V3.99992H11.3333V5.33325H4.66667V3.99992ZM10 2.66659H6V3.99992H10V2.66659Z"
        fill="#000091"
      />
    </svg>
  );

  return (
    <TdModal isOpen onClose={onClose} ariaLabel={title} title={title} size="L">
      <div className="fr-stepper fr-mb-3w">
        <h2 className="fr-stepper__title">
          {STEP_LABELS[step]}
          <span className="fr-stepper__state">
            Étape {stepIndex + 1} sur {totalSteps}
          </span>
        </h2>
        <div
          className="fr-stepper__steps"
          data-fr-current-step={stepIndex + 1}
          data-fr-steps={totalSteps}
        />
        {nextStepLabel && (
          <p className="fr-stepper__details">
            <span className="fr-text--bold">Étape suivante :</span>{" "}
            {nextStepLabel}
          </p>
        )}
      </div>

      {/* Étape 1 : Explication */}
      {step === "explanation" && (
        <div>
          <p className="fr-mb-3w">
            Pour renforcer la sécurité de votre compte, un code à usage unique
            vous sera demandé à chaque connexion. Pour générer ce code, vous
            devrez utiliser une application d'authentification compatible TOTP,
            installée sur votre téléphone.
          </p>
          <p className="fr-mb-3w">
            Si vous n'en utilisez pas déjà une, vous pouvez installer l'une des
            applications suivantes :{" "}
            <a
              href="https://freeotp.github.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="fr-link"
            >
              FreeOTP
            </a>
            <p>
              <a
                href="https://2fas.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="fr-link"
              >
                2FAS
              </a>
            </p>
            <p>
              <a
                href="https://support.google.com/accounts/answer/1066447"
                target="_blank"
                rel="noopener noreferrer"
                className="fr-link"
              >
                Google Authenticator
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/en-us/authenticator/download-microsoft-authenticator"
                target="_blank"
                rel="noopener noreferrer"
                className="fr-link"
              >
                Microsoft Authenticator
              </a>
            </p>
          </p>

          {generateError && (
            <DsfrNotificationError apolloError={generateError} />
          )}

          <div
            className={`fr-checkbox-group fr-mb-2w${
              showAppError ? " fr-checkbox-group--error" : ""
            }`}
          >
            <input
              type="checkbox"
              id="app-installed"
              className="fr-checkbox-group__input"
              checked={appInstalled}
              onChange={e => {
                setAppInstalled(e.target.checked);
                if (e.target.checked) setShowAppError(false);
              }}
            />
            <label className="fr-label" htmlFor="app-installed">
              J'ai installé une application d'authentification sur mon
              smartphone
            </label>
            {showAppError && (
              <p className="fr-error-text">
                Il est nécessaire d'installer une application d'authentification
                TOTP pour passer à l'étape suivante de mise en place de
                l'authentification multifactorielle.
              </p>
            )}
          </div>

          <div className="fr-btns-group fr-btns-group--right fr-btns-group--inline">
            <Button
              priority="secondary"
              disabled={step === "explanation"}
              onClick={onClose}
            >
              Précédent
            </Button>
            <Button disabled={generating} onClick={goToQrCode}>
              Continuer
            </Button>
          </div>
        </div>
      )}

      {/* Étape 2 : QR code + saisie du code (fusionnés) */}
      {step === "qrcode" && (
        <div>
          <h3 className="fr-h1">
            <p className="fr-text--lead">
              Scannez ce QRCode avec votre application d'authentification.
            </p>
          </h3>

          {qrCodeUrl && (
            <div className="fr-mb-2w" style={{ textAlign: "center" }}>
              <QRCode value={qrCodeUrl} size={180} />
            </div>
          )}

          {/* Clé manuelle : lien toggle simple, pas d'Accordion */}
          {secret && (
            <div className="fr-mb-3w">
              <button
                type="button"
                className="fr-link"
                onClick={() => setShowManualKey(v => !v)}
                aria-expanded={showManualKey}
              >
                Vous ne pouvez pas scanner le code ?
              </button>
              {showManualKey && (
                <div className="fr-mt-1w fr-text--sm">
                  <p className="fr-mb-1w">
                    Pour configurer manuellement votre application
                    d'authentification mobile :
                  </p>
                  <p className="fr-mb-0">
                    <strong>Clé/secret :</strong>{" "}
                    <code style={{ wordBreak: "break-all" }}>{secret}</code>
                  </p>
                  <p className="fr-mb-0">
                    <strong>Type :</strong> TOTP
                  </p>
                  <p className="fr-mb-0">
                    <strong>Algorithme :</strong> SHA1
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="fr-col-md-9">
            <label className="fr-label fr-text--bold fr-²">
              Insérez le code généré par votre application d'authentification
            </label>
            <Input
              label="Entrez le code à usage unique"
              state={confirmError ? "error" : "default"}
              stateRelatedMessage={
                confirmError ? "Le code est incorrect" : undefined
              }
              nativeInputProps={{
                value: totpCode,
                onChange: e => {
                  setTotpCode(e.target.value);
                  // Efface l'erreur dès que l'utilisateur modifie le champ
                  if (confirmError) resetConfirmError();
                },
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 6,
                autoComplete: "one-time-code",
                placeholder: "ex: 123456"
              }}
            />
          </div>

          <div className="fr-btns-group fr-btns-group--right fr-btns-group--inline fr-mt-3w">
            <Button priority="secondary" onClick={() => setStep("explanation")}>
              Précédent
            </Button>
            <Button
              disabled={totpCode.length !== 6 || confirming}
              onClick={handleConfirmCode}
            >
              Continuer
            </Button>
          </div>
        </div>
      )}

      {/* Étape 3 : Codes de récupération */}
      {step === "recovery" && (
        <div>
          <h3 className="fr-h6 fr-mb-1w">
            Sauvegardez vos clés de récupération
          </h3>
          <p className="fr-mb-3w">
            Ces clés sont le seul et unique moyen de récupérer votre compte si
            vous perdez votre téléphone. Nous vous conseillons fortement de les
            sauvegarder dans un document ou de les imprimer.
          </p>

          <div className="fr-mb-3w">
            <div
              className="fr-p-2w fr-mb-2w"
              style={{
                background: "#f6f6f6",
                borderRadius: 4,
                fontFamily: "monospace",
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "0.5rem"
              }}
            >
              {recoveryCodes.map(code => (
                <span key={code} style={{ letterSpacing: "0.05em" }}>
                  {code}
                </span>
              ))}
            </div>
          </div>

          <ul className="fr-btns-group--inline fr-mb-3w">
            <li>
              <Button
                priority="secondary"
                iconId="fr-icon-download-line"
                iconPosition="left"
                onClick={handleDownloadCodes}
              >
                Télécharger en .txt
              </Button>
            </li>
            <li>
              <Button
                priority="secondary"
                onClick={handleCopyCodes}
                className="fr-ml-1w"
              >
                <CopyRecoveryCodeIcone />
                {copied ? "Copié !" : "Copier"}
              </Button>
            </li>
          </ul>

          {/* Checkbox avec validation au clic */}
          <div
            className={`fr-checkbox-group fr-mb-3w${
              showSavedError ? " fr-checkbox-group--error" : ""
            }`}
          >
            <input
              type="checkbox"
              id="saved-confirmed"
              className="fr-checkbox-group__input"
              checked={savedConfirmed}
              onChange={e => {
                setSavedConfirmed(e.target.checked);
                if (e.target.checked) setShowSavedError(false);
              }}
            />
            <label className="fr-label" htmlFor="saved-confirmed">
              J'ai sauvegardé mes clés de récupération
            </label>
            {showSavedError && (
              <p className="fr-error-text">
                Il est indispensable de sauvegarder vos clés de récupération
                sans cela, en cas de perte de votre téléphone, vous ne pourrez
                plus accéder à votre compte.
              </p>
            )}
          </div>

          {finalizeError && (
            <DsfrNotificationError apolloError={finalizeError} />
          )}

          <div className="fr-btns-group fr-btns-group--right fr-btns-group--inline fr-mt-3w">
            <Button priority="secondary" onClick={() => setStep("recovery")}>
              Précédent
            </Button>
            <Button disabled={finalizing} onClick={handleFinish}>
              Activer
            </Button>
          </div>
        </div>
      )}

      {/* Étape 4 : Confirmation */}
      {step === "success" && (
        <div>
          {/* Bannière succès compacte (pas de title séparé) */}
          <div className="fr-alert fr-alert--success fr-mb-3w" role="alert">
            <p>Votre double authentification est bien activée</p>
          </div>
          <div className="fr-btns-group fr-btns-group--right fr-btns-group--inline">
            <Button onClick={onSuccess}>Fermer</Button>
          </div>
        </div>
      )}
      <textarea
        ref={copyTextareaRef}
        aria-hidden="true"
        readOnly
        style={{ position: "absolute", left: "-9999px", top: 0, opacity: 0 }}
        tabIndex={-1}
      />
    </TdModal>
  );
}
